import cn from 'classnames';
import React, { FC, ReactNode, useCallback } from 'react';

import { ButtonOwnProps, Button as MuiButton } from '@mui/material';

import { useAuth } from '@contexts/common/AuthorizationContext';
import { modalActions } from '@contexts/common/ModalContext';

import Loader from '@components/common/Loader';

import { MODAL_TYPES } from '@constants/common/modals.constants';
import { Size, Variant } from '@constants/common/ui.constants';

import useStyles from './styles.module';

export type ButtonProps = Omit<ButtonOwnProps, 'variant'> & {
  variant?: Variant;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isLoading?: boolean;
  className?: string;
  classNameChildren?: string;
  buttonType?: 'button' | 'submit' | 'reset';
  checkAuth?: boolean;
  minWidth?: number | string;
  id?: string;
  iconLeft?: string | ReactNode;
  iconRight?: string | ReactNode;
  component?: string;
  href?: string;
  newTab?: boolean;
};

const Button: FC<ButtonProps> = ({
  children,
  disabled = false,
  onClick = undefined,
  variant = Variant.Contained,
  size,
  isLoading = false,
  className = '',
  buttonType = 'button',
  checkAuth = false,
  minWidth = 'initial',
  id = '',
  iconLeft,
  classNameChildren = '',
  iconRight,
  component,
  ...props
}) => {
  const { account } = useAuth();
  const { classes, cx } = useStyles();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (checkAuth && !account) {
        modalActions.show(MODAL_TYPES.CONNECT_WALLET_MODAL);
        return;
      }
      if (isLoading || disabled || !onClick) return;
      onClick(event);
    },
    [checkAuth, account, isLoading, disabled, onClick],
  );

  return (
    <MuiButton
      size={size || Size.Large}
      // @ts-ignore
      variant={variant || Variant.Contained}
      disabled={disabled || isLoading}
      onClick={handleClick}
      type={buttonType}
      disableRipple={isLoading}
      disableFocusRipple
      disableTouchRipple
      className={cx(
        classes.button,
        { [classes.loading]: isLoading },
        className,
      )}
      style={{ minWidth: minWidth || 'initial' }}
      id={id}
      component={component}
      {...props}
    >
      {isLoading ? (
        <Loader size={24} className={classes.loader} />
      ) : (
        <span className={classes.content}>
          {iconLeft && (
            <span className={classes.iconLeft}>
              {typeof iconLeft === 'string' ? (
                <img src={iconLeft} alt="" />
              ) : (
                iconLeft
              )}
            </span>
          )}
          <span className={cn(classes.children, classNameChildren)}>
            {children}
          </span>
          {iconRight && (
            <span className={classes.iconRight}>
              {typeof iconRight === 'string' ? (
                <img src={iconRight} alt="" />
              ) : (
                iconRight
              )}
            </span>
          )}
        </span>
      )}
    </MuiButton>
  );
};

export default Button;
